import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPaintBrush, 
  FaGlobe, 
  FaExternalLinkAlt, 
  FaUndo,
  FaRedo,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import { 
  MdClear, 
  MdBrush,
} from "react-icons/md";
import { ReactSketchCanvas } from 'react-sketch-canvas';

interface WhiteboardProps {
  selectedPlatform: string;
  setSelectedPlatform: (val: string) => void;
  embedUrl: string;
  setEmbedUrl: (val: string) => void;
  isFocused: boolean;
  isRecording?: boolean;
}

const platformOptions = [
  { label: 'Basic Whiteboard (Local)', value: 'basic-canvas' },
  { label: 'Miro', value: 'miro' },
  { label: 'Jamboard', value: 'jamboard' },
  { label: 'Lucid', value: 'lucid' },
  { label: 'Other', value: 'other' },
];

const colors = [
  { name: 'Black', value: '#000000' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Orange', value: '#F97316' },
];

const brushSizes = [2, 4, 6, 8, 12, 16, 20];

const Whiteboard: React.FC<WhiteboardProps> = ({
  selectedPlatform,
  setSelectedPlatform,
  embedUrl,
  setEmbedUrl,
  isFocused,
  isRecording = false,
}) => {
  const canvasRef = useRef<any>();
  const showCanvas = selectedPlatform === 'basic-canvas';
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentBrushSize, setCurrentBrushSize] = useState(4);
  const [showColorPalette, setShowColorPalette] = useState(false);
  const [showBrushSizes, setShowBrushSizes] = useState(false);

  // Update canvas stroke properties when color or brush size changes
  useEffect(() => {
    if (canvasRef.current && showCanvas) {
      // Update stroke color and width dynamically
      if (typeof canvasRef.current.setStrokeColor === 'function') {
        canvasRef.current.setStrokeColor(currentColor);
      }
      if (typeof canvasRef.current.setStrokeWidth === 'function') {
        canvasRef.current.setStrokeWidth(currentBrushSize);
      }
    }
  }, [currentColor, currentBrushSize, showCanvas]);

  // Close palettes when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.color-palette-container') && !target.closest('.brush-size-container')) {
        setShowColorPalette(false);
        setShowBrushSizes(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const styles = {
    border: 'none',
    borderRadius: '0.5rem',
  };

  const containerClass = isFocused
    ? 'relative w-full h-full bg-white'
    : 'w-full h-full bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 flex flex-col';

  return (
    <>
      {/* Inline CSS for custom cursor */}
      <style>{`
        .custom-canvas:hover {
          cursor: url('https://img.icons8.com/ios-filled/24/000000/pencil-tip.png') 2 22, crosshair;
        }
        .whiteboard-tool-button {
          transition: all 0.2s ease;
        }
        .whiteboard-tool-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
      `}</style>

      <div className={containerClass}>
        {!isFocused && !isRecording && (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 overflow-hidden"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
                      <FaPaintBrush className="text-indigo-600" />
                      Whiteboard Setup
                    </h2>
                    <p className="text-gray-600 text-sm mt-1">
                      Choose a platform or use our built-in canvas
                    </p>
                  </div>
                </div>

                {/* Platform Selector */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 font-medium text-gray-700 text-sm">
                      Whiteboard Platform
                    </label>
                    <select
                      value={selectedPlatform}
                      onChange={(e) => setSelectedPlatform(e.target.value)}
                      className="w-full border-2 border-gray-300 bg-white rounded-lg px-4 py-2.5 text-gray-700 outline-none
                                 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition text-sm sm:text-base"
                    >
                      {platformOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Embed URL input */}
                  {!showCanvas && (
                    <div>
                      <label className="block mb-2 font-medium text-gray-700 text-sm flex items-center gap-1">
                        <FaExternalLinkAlt className="text-gray-600 text-xs" />
                        Shareable Link
                      </label>
                      <input
                        type="text"
                        placeholder="https://miro.com/app/board/..."
                        value={embedUrl}
                        onChange={(e) => setEmbedUrl(e.target.value)}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-2.5 bg-white text-gray-700
                                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition text-sm sm:text-base"
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Paste the shareable or embed URL
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Whiteboard Canvas Area */}
        <div className="flex-1 relative flex flex-col">
          {showCanvas ? (
            <>
              {/* Toolbar */}
              <div className="bg-white border-b border-gray-200 p-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Color Picker */}
                  <div className="relative color-palette-container">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowColorPalette(!showColorPalette);
                        setShowBrushSizes(false);
                      }}
                      className="whiteboard-tool-button flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div
                        className="w-5 h-5 rounded border-2 border-gray-300"
                        style={{ backgroundColor: currentColor }}
                      />
                      <span className="hidden sm:inline">Color</span>
                      {showColorPalette ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                    </motion.button>
                    <AnimatePresence>
                      {showColorPalette && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50"
                        >
                          <div className="grid grid-cols-4 gap-2">
                            {colors.map((color) => (
                              <motion.button
                                key={color.value}
                                onClick={() => {
                                  setCurrentColor(color.value);
                                  setShowColorPalette(false);
                                }}
                                className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                  currentColor === color.value
                                    ? 'border-gray-800 scale-110 shadow-lg'
                                    : 'border-gray-300 hover:border-gray-500'
                                }`}
                                style={{ backgroundColor: color.value }}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.95 }}
                                title={color.name}
                              />
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Brush Size */}
                  <div className="relative brush-size-container">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowBrushSizes(!showBrushSizes);
                        setShowColorPalette(false);
                      }}
                      className="whiteboard-tool-button flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <MdBrush className="text-base" />
                      <span className="hidden sm:inline">{currentBrushSize}px</span>
                      {showBrushSizes ? <FaChevronUp className="text-xs" /> : <FaChevronDown className="text-xs" />}
                    </motion.button>
                    <AnimatePresence>
                      {showBrushSizes && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50"
                        >
                          <div className="space-y-2">
                            {brushSizes.map((size) => (
                              <motion.button
                                key={size}
                                onClick={() => {
                                  setCurrentBrushSize(size);
                                  setShowBrushSizes(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition ${
                                  currentBrushSize === size
                                    ? 'bg-indigo-100 text-indigo-700 font-semibold'
                                    : 'hover:bg-gray-100 text-gray-700'
                                }`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <span className="text-sm">{size}px</span>
                                <div
                                  className="rounded-full bg-current"
                                  style={{
                                    width: `${Math.min(size, 16)}px`,
                                    height: `${Math.min(size, 16)}px`,
                                  }}
                                />
                              </motion.button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Divider */}
                  <div className="w-px h-6 bg-gray-300" />

                  {/* Undo/Redo */}
                  <motion.button
                    onClick={() => canvasRef.current?.undo()}
                    className="whiteboard-tool-button p-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Undo"
                  >
                    <FaUndo className="text-sm" />
                  </motion.button>
                  <motion.button
                    onClick={() => canvasRef.current?.redo()}
                    className="whiteboard-tool-button p-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Redo"
                  >
                    <FaRedo className="text-sm" />
                  </motion.button>

                  {/* Clear */}
                  <motion.button
                    onClick={() => {
                      if (window.confirm('Clear the entire whiteboard? This cannot be undone.')) {
                        canvasRef.current?.clearCanvas();
                      }
                    }}
                    className="whiteboard-tool-button flex items-center gap-2 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <MdClear className="text-base" />
                    <span className="hidden sm:inline">Clear</span>
                  </motion.button>
                </div>

                {/* Current Settings Display */}
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <div className="hidden sm:flex items-center gap-2">
                    <span>Color:</span>
                    <div
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ backgroundColor: currentColor }}
                    />
                  </div>
                  <div className="hidden sm:flex items-center gap-2">
                    <span>Size:</span>
                    <span className="font-semibold">{currentBrushSize}px</span>
                  </div>
                </div>
              </div>

              {/* Canvas */}
              <div className="flex-1 relative bg-gray-50">
                <div
                  className="absolute inset-0 rounded-b-xl overflow-hidden"
                  style={{
                    height: isFocused ? 'calc(100vh - 200px)' : '100%',
                    minHeight: '400px',
                  }}
                >
                  <ReactSketchCanvas
                    style={styles}
                    ref={canvasRef}
                    width="100%"
                    height="100%"
                    strokeWidth={currentBrushSize}
                    strokeColor={currentColor}
                    canvasColor="transparent"
                    className="custom-canvas"
                  />
                </div>
                {!isFocused && (
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-gray-200">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <FaPaintBrush className="text-indigo-600" />
                      <span>Start drawing on the canvas</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Embedded Whiteboard */
            <div
              className="flex-1 relative bg-gray-50 rounded-b-xl overflow-hidden"
              style={{
                height: isFocused ? 'calc(100vh - 200px)' : '100%',
                minHeight: '400px',
              }}
            >
              {embedUrl ? (
                <iframe
                  title="Embedded Whiteboard"
                  src={embedUrl}
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 bg-gradient-to-br from-gray-50 to-gray-100">
                  <FaGlobe className="text-5xl mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">No whiteboard URL provided</p>
                  <p className="text-sm text-gray-500">
                    Enter a valid whiteboard link above to get started
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Whiteboard;